import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { CompanyAccount } from '@treasury/domain/company/company-account.entity';
import { TmApiError } from '@treasury/domain/shared';
import { StopPayment, StopPaymentsService } from '@treasury/domain/stop-payments';
import { TmContainer } from '@treasury/presentation';
import '@treasury/presentation/components/tm-blocking-loader';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import '../partials/stop-payment-confirm-step';
import '../partials/stop-payment-create-step';
import '../partials/stop-payment-review-step';
enum Step {
    Create,
    Review,
    Confirm,
}

export const tagName = 'stop-payment-workflow-container';
@customElement(tagName)
export class StopPaymentWorkflowContainer extends TmContainer {
    @InjectProperty()
    private stopPaymentsService!: StopPaymentsService;

    @InjectProperty()
    private navigationService!: NavigationService;

    @InjectProperty()
    private headerBarService!: HeaderBarService;

    @state()
    private step: Step = Step.Create;

    @state()
    private stopPayment = new StopPayment();

    @state()
    private accounts: CompanyAccount[] = [];

    protected render() {
        return html`
            ${this.loading ? html`<tm-blocking-loader></tm-blocking-loader>` : nothing}
            ${this.step === Step.Create
                ? html`<stop-payment-create-step
                      .stopPayment=${this.stopPayment}
                      .accounts=${this.accounts}
                      @create=${this.onCreate}
                  ></stop-payment-create-step>`
                : nothing}
            ${this.step === Step.Review
                ? html`<stop-payment-review-step
                      .stopPayment=${this.stopPayment}
                      @confirm=${this.onConfirm}
                  ></stop-payment-review-step>`
                : nothing}
            ${this.step === Step.Confirm
                ? html`<stop-payment-confirm-step
                      .stopPayment=${this.stopPayment}
                      @create-new=${() => {
                          this.stopPayment = new StopPayment();
                          this.step = Step.Create;
                          this.headerBarService.configure({
                              title: 'Add Stop Payment',
                              backAction: () => {
                                  this.navigationService.navigate(`/stop-payments`);
                              },
                          });
                      }}
                  ></stop-payment-confirm-step>`
                : nothing}
        `;
    }

    protected async firstUpdated() {
        this.loading = true;
        this.headerBarService.configure({
            title: 'Add Stop Payment',
            backAction: () => {
                this.navigationService.navigate(`/stop-payments`);
            },
        });
        try {
            this.accounts = await this.stopPaymentsService.getFilterAccounts();
        } catch (error) {
            this.notificationService.renderError(error as unknown as TmApiError);
        } finally {
            this.loading = false;
        }
    }

    private onCreate() {
        if (this.stopPayment.checkNumberType === 'range') {
            this.stopPayment.checkDate = undefined;
            this.stopPayment.payee = '';
            this.stopPayment.checkAmount = 0;
        }
        this.headerBarService.configure({
            title: 'Review Stop Payment',
            backAction: () => {
                this.step = Step.Create;
                this.headerBarService.configure({
                    title: 'Add Stop Payment',
                    backAction: () => {
                        this.navigationService.navigate(`/stop-payments`);
                    },
                });
            },
        });
        this.step = Step.Review;
    }

    private async onConfirm() {
        if (this.stopPayment) {
            this.loading = true;
            try {
                const response = await this.stopPaymentsService.createStopPayment(this.stopPayment);
                this.stopPayment.status = response.status;
                this.step = Step.Confirm;
            } catch (e) {
                this.notificationService.renderError(e as unknown as TmApiError);
            } finally {
                this.loading = false;
            }
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: grid;
                    height: 100%;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: StopPaymentWorkflowContainer;
    }
}
